// src/receptionist/components/BookingFlowBuilder.tsx
//
// Booking Flow builder — the merchant-facing adaptation of
// containers/hiring-flow-builder.tsx (#3070 / #3130). The interaction model is
// deliberately identical, because it is already proven with HR users:
//
//   • a top→down chart: a start node, step nodes joined by connectors
//   • inline "+" insertion between any two steps, and drag to reorder
//   • gates drawn as labelled pass / flag branches hanging off their step
//   • a side panel that configures whichever node is selected
//
// What differs is the catalogue (booking steps, not hiring steps) and the
// wording, which is written for a salon owner rather than a recruiter.
//
// Kept intentionally free of data fetching: the parent owns the flow and
// persists it, exactly as the Create Job wizard does for hiring.

import { useMemo, useState } from "react";
import {
    AlertTriangleIcon,
    CalendarClockIcon,
    CheckCircle2Icon,
    ClipboardListIcon,
    FileTextIcon,
    GripVerticalIcon,
    LinkIcon,
    MessageCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    FilterIcon,
    PlayIcon,
    PlusIcon,
    ScissorsIcon,
    TrashIcon,
    UserIcon,
    XIcon,
} from "lucide-react";
import {
    DOCUMENT_PRESETS,
    KNOCKOUT_OPERATOR_LABELS,
    MAX_FLOW_STEPS,
    REMINDER_PRESETS,
    STEP_TYPE_DESCRIPTIONS,
    STEP_TYPE_LABELS,
    newStepId,
    stepGates,
    validateFlow,
    type AnswerKnockoutGate,
    type BookingFlow,
    type BookingFlowStep,
    type BookingGate,
    type BookingStepType,
    type DeadlineGate,
    type KnockoutOperator,
} from "../flow";
import { Popover, PopoverTrigger, PopoverContent } from "@component/popover";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Textarea } from "@component/textarea";
import { Checkbox } from "@component/checkbox";
import { Label } from "@component/label";
import { Switch } from "@component/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { AddButton, TagPill } from "./ui";
import { cn } from "@oura/lib/cn";

/** The hiring-flow builder's checked-state blue, so a tick looks the same in both builders. */
const CHECKBOX_STYLE = "data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white";

const STEP_TYPE_ICONS: Record<BookingStepType, React.ComponentType<{ className?: string }>> = {
    collect_service: ScissorsIcon,
    collect_time: CalendarClockIcon,
    collect_details: UserIcon,
    questions: ClipboardListIcon,
    screening: CheckCircle2Icon,
    documents: FileTextIcon,
    message: MessageCircleIcon,
    external_link: LinkIcon,
};

const CATALOG: BookingStepType[] = [
    "collect_service",
    "collect_time",
    "collect_details",
    "questions",
    "screening",
    "documents",
    "message",
    "external_link",
];

type NodeSelection = "entry" | string | null;

interface Props {
    flow: BookingFlow;
    onChange: (flow: BookingFlow) => void;
    /** Bookable services from Services & FAQs, used to explain collect_service. */
    services?: string[];
    disabled?: boolean;
}

/** A step type that can carry questions can also carry a knockout condition. */
function canHaveKnockout(type: BookingStepType): boolean {
    return type === "screening" || type === "questions";
}

function blankStep(type: BookingStepType): BookingFlowStep {
    const step: BookingFlowStep = { id: newStepId(), type, advance: "auto" };
    if (type === "questions" || type === "screening") {
        step.questions = [{ ref: `q_${Math.random().toString(36).slice(2, 6)}`, prompt: "" }];
    }
    if (type === "documents") step.items = [DOCUMENT_PRESETS[0].key];
    if (type === "message") step.message = "";
    if (type === "external_link") { step.label = ""; step.url = ""; }
    return step;
}

export default function BookingFlowBuilder({ flow, onChange, services = [], disabled }: Props) {
    const [selectedId, setSelectedId] = useState<NodeSelection>("entry");
    const [addMenuIndex, setAddMenuIndex] = useState<number | null>(null);
    const [dragIndex, setDragIndex] = useState<number | null>(null);

    const errors = useMemo(() => validateFlow(flow), [flow]);
    const selectedStep = flow.steps.find((s) => s.id === selectedId) || null;
    const selectedIndex = selectedStep ? flow.steps.findIndex((s) => s.id === selectedStep.id) : -1;

    const update = (next: Partial<BookingFlow>) => onChange({ ...flow, ...next });
    const setSteps = (steps: BookingFlowStep[]) => update({ steps });

    const patchStep = (id: string, patch: Partial<BookingFlowStep>) =>
        setSteps(flow.steps.map((s) => (s.id === id ? { ...s, ...patch } : s)));

    const insertAt = (index: number, type: BookingStepType) => {
        if (flow.steps.length >= MAX_FLOW_STEPS) return;
        const next = [...flow.steps];
        const step = blankStep(type);
        next.splice(index, 0, step);
        setSteps(next);
        setSelectedId(step.id);
        setAddMenuIndex(null);
    };

    const removeStep = (id: string) => {
        setSteps(flow.steps.filter((s) => s.id !== id));
        if (selectedId === id) setSelectedId("entry");
    };

    const moveStep = (from: number, to: number) => {
        if (from === to || to < 0 || to >= flow.steps.length) return;
        const next = [...flow.steps];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setSteps(next);
    };

    // ---------- gates ----------
    const setGates = (id: string, gates: BookingGate[]) => patchStep(id, { gates });

    const addKnockout = (step: BookingFlowStep) => {
        const first = (step.questions || [])[0];
        const gate: AnswerKnockoutGate = {
            type: "answer_knockout",
            question_ref: first?.ref || "",
            operator: "eq",
            value: "",
            on_fail: { action: "flag" },
            escalate: true,
        };
        setGates(step.id, [...stepGates(step), gate]);
    };

    const addDeadline = (step: BookingFlowStep) => {
        if (stepGates(step).some((g) => g.type === "deadline")) return;
        const gate: DeadlineGate = { type: "deadline", due: "+2h", reminders: ["-1h"], on_miss: { action: "hold" } };
        setGates(step.id, [...stepGates(step), gate]);
    };

    const patchGate = (step: BookingFlowStep, index: number, patch: Partial<BookingGate>) => {
        const gates = stepGates(step).map((g, i) => (i === index ? ({ ...g, ...patch } as BookingGate) : g));
        setGates(step.id, gates);
    };

    const removeGate = (step: BookingFlowStep, index: number) =>
        setGates(step.id, stepGates(step).filter((_, i) => i !== index));

    // ---------- canvas helpers ----------
    //
    // The chart vocabulary is the hiring flow builder's, deliberately
    // (containers/hiring-flow-builder.tsx, presentation #3130): a dotted
    // canvas, a start node, step nodes joined by connectors that carry the
    // inline "+", gates drawn as labelled pass/fail branches, and a terminal
    // node. Same product, same interaction, so a merchant who has seen one
    // does not have to learn the other — and it is proven with real users.

    const atCap = flow.steps.length >= MAX_FLOW_STEPS;

    /** Save-time problems that belong to ONE step, so its node can say so. */
    const stepInvalid = (index: number) =>
        errors.some((e) => e.startsWith(`Step ${index + 1} (`));

    const nodeCls = (selected: boolean, invalid?: boolean) =>
        cn(
            "w-full max-w-sm mx-auto block rounded-xl border bg-white dark:bg-gray-900 p-3.5 text-left shadow-sm transition-all cursor-pointer",
            selected
                ? "border-blue-500 ring-2 ring-blue-500/20"
                : invalid
                    ? "border-rose-300 hover:border-rose-400 hover:shadow-md"
                    : "border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:shadow-md",
        );

    /** A chip on the start node — the facts that hold for the whole flow. */
    const entryChip = (label: string) => (
        <span key={label} className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-200 dark:bg-gray-800 dark:text-gray-300">
            {label}
        </span>
    );

    /**
     * The edge between two nodes: a hairline, the review checkpoint when the
     * step above waits for a human, and the "+" that inserts here. `at` is
     * both the popover's identity and the insertion index.
     */
    const connector = (at: number, checkpoint?: boolean) => (
        <div className="flex flex-col items-center">
            <div className="h-3.5 w-px bg-gray-300 dark:bg-gray-600" />
            {checkpoint && (
                <>
                    <span className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:text-gray-400">
                        Waits for your team
                    </span>
                    <div className="h-3.5 w-px bg-gray-300 dark:bg-gray-600" />
                </>
            )}
            <Popover open={addMenuIndex === at} onOpenChange={(open) => setAddMenuIndex(open ? at : null)}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        disabled={disabled || atCap}
                        title={atCap ? `A flow can have at most ${MAX_FLOW_STEPS} steps.` : "Add a step here"}
                        aria-label="Add a step here"
                        className={cn(
                            "flex h-6 w-6 items-center justify-center rounded-md bg-blue-600 text-white shadow-sm transition-all hover:bg-blue-700 hover:scale-110",
                            (disabled || atCap) && "opacity-40 pointer-events-none",
                        )}
                    >
                        <PlusIcon className="h-3.5 w-3.5" />
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-2" align="center">
                    <p className="px-2 pb-1.5 pt-1 text-[13px] leading-[18px] font-medium text-gray-500">Add a step</p>
                    <div className="space-y-0.5">
                        {CATALOG.map((type) => {
                            const Icon = STEP_TYPE_ICONS[type];
                            return (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => insertAt(at, type)}
                                    className="flex w-full items-start gap-3 rounded-lg p-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                                >
                                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E8F1FF] text-[#0A5FD1] dark:bg-blue-950/40">
                                        <Icon className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{STEP_TYPE_LABELS[type]}</p>
                                        <p className="text-xs text-gray-500">{STEP_TYPE_DESCRIPTIONS[type]}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </PopoverContent>
            </Popover>
            <div className="h-3.5 w-px bg-gray-300 dark:bg-gray-600" />
        </div>
    );

    /**
     * A gated step's two outcomes, drawn as branches.
     *
     * The fail lane is terminal and the pass lane rejoins the trunk, which is
     * the honest shape: a knockout FLAGS a booking for a human, it never
     * declines the customer. Several gates collapse into one branch — the
     * diagram never fans out per condition.
     */
    const gateBranch = (step: BookingFlowStep) => {
        const gates = stepGates(step);
        if (gates.length === 0) return null;
        const isKnockout = gates.some((g) => g.type === "answer_knockout");
        const escalates = gates.some((g) => g.type === "answer_knockout" && g.escalate);
        return (
            <div className="mx-auto w-full max-w-sm">
                <div className="flex justify-center"><div className="h-3 w-px bg-gray-300 dark:bg-gray-600" /></div>
                <div className="mx-auto h-px w-1/2 bg-gray-300 dark:bg-gray-600" />
                <div className="grid grid-cols-2">
                    <div className="flex flex-col items-center">
                        <div className="h-3 w-px bg-gray-300 dark:bg-gray-600" />
                        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
                            {isKnockout ? "Pass — continue" : "On time — continue"}
                        </span>
                        <div className="w-px flex-1 bg-gray-300 dark:bg-gray-600" />
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="h-3 w-px bg-amber-300 dark:bg-amber-800" />
                        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                            {isKnockout ? "Answer flags it" : "Nothing arrives"}
                        </span>
                        <div className="h-2 w-px bg-amber-300 dark:bg-amber-800" />
                        <div className="max-w-full rounded-xl bg-amber-50 ring-1 ring-inset ring-amber-200 px-2.5 py-1.5 text-center text-[11px] font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                            {isKnockout ? "Flag & pause for your team" : "Hold the request"}
                        </div>
                        {/* The customer is never declined here — that is the
                            whole point of a flag, and it is worth saying on the
                            diagram rather than only in the side panel. */}
                        <span className="mt-1 max-w-full text-center text-[10px] leading-tight text-gray-400 dark:text-gray-500">
                            {escalates
                                ? <>Alerts the <span className="font-medium text-amber-700 dark:text-amber-400">staff channel</span>; the customer is told a person will follow up</>
                                : <>Waits in <span className="font-medium text-amber-700 dark:text-amber-400">Bookings</span>; the customer is never declined</>}
                        </span>
                    </div>
                </div>
                <div className="grid grid-cols-2">
                    <div className="flex justify-center"><div className="h-3 w-px bg-gray-300 dark:bg-gray-600" /></div>
                    <div />
                </div>
                <div className="ml-[25%] h-px w-1/4 bg-gray-300 dark:bg-gray-600" />
            </div>
        );
    };


    return (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            {/* ============================ Canvas ============================ */}
            <div>
                <div
                    className="relative rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-950/40 px-4 py-8 [background-image:radial-gradient(circle,#d4d4d8_1px,transparent_1px)] [background-size:16px_16px] dark:[background-image:radial-gradient(circle,#374151_1px,transparent_1px)]"
                    onClick={() => setSelectedId(null)}
                >
                    {/* Clicks on a node must not reach the deselect above. */}
                    <div className="space-y-0" onClick={(e) => e.stopPropagation()}>
                        {/* Start node — what is true of the whole flow. */}
                        <button
                            type="button"
                            onClick={() => setSelectedId("entry")}
                            className={nodeCls(selectedId === "entry")}
                        >
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E8F1FF] text-[#0A5FD1] dark:bg-blue-950/40">
                                    <PlayIcon className="h-4 w-4 text-blue-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[13px] leading-[18px] font-medium text-gray-500">Start</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Customer messages you</p>
                                    <p className="truncate text-xs text-gray-500">The flow starts when someone asks to book on WhatsApp.</p>
                                </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {entryChip("WhatsApp")}
                                {entryChip(flow.opener.offerServiceMenu ? "Offers the service list" : "Answers first, then asks")}
                                {entryChip(`${services.length} bookable`)}
                            </div>
                        </button>

                        {connector(0)}

                        {flow.steps.map((step, index) => {
                            const Icon = STEP_TYPE_ICONS[step.type];
                            const isLast = index === flow.steps.length - 1;
                            const invalid = stepInvalid(index);
                            const gates = stepGates(step);
                            return (
                                <div key={step.id}>
                                    <div
                                        draggable={!disabled}
                                        onDragStart={() => setDragIndex(index)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => {
                                            if (dragIndex !== null && dragIndex !== index) moveStep(dragIndex, index);
                                            setDragIndex(null);
                                        }}
                                        onClick={() => setSelectedId(step.id)}
                                        className={cn(nodeCls(selectedId === step.id, invalid), dragIndex === index && "opacity-60 border-blue-400")}
                                    >
                                        <div className="flex items-center gap-3">
                                            <GripVerticalIcon className="h-4 w-4 flex-shrink-0 cursor-grab text-gray-300" />
                                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E8F1FF] text-[#0A5FD1] dark:bg-blue-950/40">
                                                <Icon className="h-4 w-4 text-blue-600" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[13px] leading-[18px] font-medium text-gray-500">Step {index + 1}</p>
                                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{STEP_TYPE_LABELS[step.type]}</p>
                                                <p className="truncate text-xs text-gray-500">
                                                    {step.type === "message"
                                                        ? step.message || "No message yet"
                                                        : step.type === "external_link"
                                                            ? step.label || step.url || "No link yet"
                                                            : step.type === "collect_service" && services.length
                                                                ? services.join(" · ")
                                                                : STEP_TYPE_DESCRIPTIONS[step.type]}
                                                </p>
                                            </div>
                                            {invalid && <AlertTriangleIcon className="h-4 w-4 flex-shrink-0 text-rose-500" />}
                                        </div>
                                        {gates.length > 0 && (
                                            <div className="mt-2 flex">
                                                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                                                    <FilterIcon className="h-2.5 w-2.5" />
                                                    {gates.length > 1
                                                        ? `${gates.length} conditions`
                                                        : gates[0].type === "answer_knockout" ? "Screening condition" : "Deadline"}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {gateBranch(step)}
                                    {/* The checkpoint pill belongs BETWEEN steps, so the
                                        last one does not draw a wait before the end node —
                                        that wait is the end node's own meaning. */}
                                    {connector(index + 1, !isLast && step.advance === "review")}
                                </div>
                            );
                        })}

                        {/* Terminal node. Says the guarantee out loud: v1 captures a
                            request and a human confirms it, so the flow ends at a
                            person rather than at a booked slot. */}
                        <div className="mx-auto w-full max-w-sm rounded-xl border border-dashed border-emerald-300 bg-emerald-50/60 p-3.5 dark:border-emerald-900 dark:bg-emerald-950/20">
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E7F7EC] text-[#1E7B38] dark:bg-emerald-900/40">
                                    <CheckCircle2Icon className="h-4 w-4 text-emerald-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium text-emerald-600">End</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Your team confirms</p>
                                    <p className="text-xs text-gray-500">The request lands in Bookings. The AI never promises a slot.</p>
                                </div>
                            </div>
                        </div>

                        {atCap && (
                            <p className="mt-3 text-center text-[11px] text-gray-400">
                                A flow can have at most {MAX_FLOW_STEPS} steps. Remove one to add another.
                            </p>
                        )}
                    </div>
                </div>

                {errors.length > 0 && (
                    <ul className="list-plain mt-3 space-y-1">
                        {errors.map((e) => (
                            <li key={e} className="flex items-start gap-2 text-xs text-amber-700">
                                <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                {e}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* ---------------- side panel ---------------- */}
            <aside className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                {selectedId === "entry" && (
                    <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">When the flow runs</h3>
                        <p className="text-xs text-gray-500">
                            The AI starts this flow as soon as someone shows booking intent. Questions that are
                            not about booking are answered from Services without entering the flow.
                        </p>
                        {services.length > 0 && (
                            <div>
                                <p className="mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">Bookable services</p>
                                <div className="flex flex-wrap gap-2">
                                    {services.map((s) => <TagPill key={s}>{s}</TagPill>)}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {selectedStep && (
                    <div className="space-y-4">
                        {/* Header, matching the hiring builder's step panel: the
                            same icon tile, STEP n eyebrow and reorder / remove /
                            close toolbar, so the two builders read as one tool. */}
                        <div className="flex items-center gap-2">
                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E8F1FF] text-[#0A5FD1] dark:bg-blue-950/40">
                                {(() => { const Icon = STEP_TYPE_ICONS[selectedStep.type]; return <Icon className="h-4 w-4 text-blue-600" />; })()}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-[13px] leading-[18px] font-medium text-gray-500">Step {selectedIndex + 1}</p>
                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {STEP_TYPE_LABELS[selectedStep.type]}
                                </p>
                            </div>
                            <div className="flex flex-shrink-0 items-center">
                                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={disabled || selectedIndex === 0} onClick={() => moveStep(selectedIndex, selectedIndex - 1)} aria-label="Move step up">
                                    <ChevronUpIcon className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={disabled || selectedIndex === flow.steps.length - 1} onClick={() => moveStep(selectedIndex, selectedIndex + 1)} aria-label="Move step down">
                                    <ChevronDownIcon className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600" disabled={disabled} onClick={() => removeStep(selectedStep.id)} aria-label="Remove step">
                                    <TrashIcon className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedId(null)} aria-label="Close panel">
                                    <XIcon className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <p className="-mt-3 text-xs text-gray-500">{STEP_TYPE_DESCRIPTIONS[selectedStep.type]}</p>

                        {/* A switch, not a checkbox — this is a setting that takes
                            effect, which is what a switch means everywhere else. */}
                        <div className="flex items-start gap-3">
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-gray-800 dark:text-gray-200">Pause here for your team</p>
                                <p className="text-[11px] text-gray-500">
                                    The AI stops and waits for a human before going further. The last step always pauses.
                                </p>
                            </div>
                            <Switch
                                checked={selectedStep.advance === "review"}
                                disabled={disabled || selectedIndex === flow.steps.length - 1}
                                onCheckedChange={(v) => patchStep(selectedStep.id, { advance: v ? "review" : "auto" })}
                                aria-label="Pause here for your team"
                                className="mt-0.5 flex-shrink-0"
                            />
                        </div>

                        {/* questions */}
                        {(selectedStep.type === "questions" || selectedStep.type === "screening") && (
                            <div className="space-y-2">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Questions, asked one at a time</p>
                                {(selectedStep.questions || []).map((q, qi) => (
                                    <div key={q.ref} className="flex gap-1">
                                        <Input
                                            value={q.prompt}
                                            disabled={disabled}
                                            placeholder="e.g. Have you had this treatment before?"
                                            onChange={(e) => {
                                                const questions = [...(selectedStep.questions || [])];
                                                questions[qi] = { ...q, prompt: e.target.value };
                                                patchStep(selectedStep.id, { questions });
                                            }}
                                            className="min-w-0 flex-1"
                                        />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            disabled={disabled}
                                            onClick={() => patchStep(selectedStep.id, {
                                                questions: (selectedStep.questions || []).filter((_, i) => i !== qi),
                                            })}
                                            className="h-9 w-9 flex-shrink-0 text-gray-400 hover:text-red-600"
                                            aria-label="Remove question"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                                <AddButton
                                    disabled={disabled}
                                    onClick={() => patchStep(selectedStep.id, {
                                        questions: [
                                            ...(selectedStep.questions || []),
                                            { ref: `q_${Math.random().toString(36).slice(2, 6)}`, prompt: "" },
                                        ],
                                    })}
                                >
                                    Add question
                                </AddButton>
                            </div>
                        )}

                        {/* documents */}
                        {selectedStep.type === "documents" && (
                            <div className="space-y-1.5">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">What to ask for</p>
                                {DOCUMENT_PRESETS.map((d) => {
                                    const on = (selectedStep.items || []).includes(d.key);
                                    return (
                                        <div key={d.key} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={`${selectedStep.id}-doc-${d.key}`}
                                                checked={on}
                                                disabled={disabled}
                                                className={CHECKBOX_STYLE}
                                                onCheckedChange={() => patchStep(selectedStep.id, {
                                                    items: on
                                                        ? (selectedStep.items || []).filter((k) => k !== d.key)
                                                        : [...(selectedStep.items || []), d.key],
                                                })}
                                            />
                                            <Label htmlFor={`${selectedStep.id}-doc-${d.key}`} className="cursor-pointer text-sm font-normal">
                                                {d.label}
                                            </Label>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* message */}
                        {selectedStep.type === "message" && (
                            <div className="space-y-2">
                                <Label htmlFor={`${selectedStep.id}-message`} className="text-sm font-medium">Message</Label>
                                <Textarea
                                    id={`${selectedStep.id}-message`}
                                    disabled={disabled}
                                    value={selectedStep.message || ""}
                                    placeholder="e.g. Please arrive 10 minutes early so we can do a patch test."
                                    onChange={(e) => patchStep(selectedStep.id, { message: e.target.value })}
                                    className="min-h-[100px] resize-y"
                                />
                            </div>
                        )}

                        {/* external link */}
                        {selectedStep.type === "external_link" && (
                            <div className="space-y-2">
                                <Input
                                    value={selectedStep.label || ""}
                                    disabled={disabled}
                                    placeholder="Button text, e.g. Pay deposit"
                                    onChange={(e) => patchStep(selectedStep.id, { label: e.target.value })}
                                />
                                <Input
                                    value={selectedStep.url || ""}
                                    disabled={disabled}
                                    placeholder="https://"
                                    onChange={(e) => patchStep(selectedStep.id, { url: e.target.value })}
                                />
                            </div>
                        )}

                        {/* gates */}
                        <div className="space-y-2 border-t border-gray-100 pt-3 dark:border-gray-800">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Conditions</p>
                            {stepGates(selectedStep).map((gate, gi) => {
                                const gateCount = stepGates(selectedStep).length;
                                return (
                                <div key={gi} className="space-y-3 rounded-xl bg-amber-50 ring-1 ring-inset ring-amber-200 p-3 dark:border-amber-900 dark:bg-amber-950">
                                    {/* Same header as the hiring builder's condition card:
                                        the rule is stated in the title, and the card is
                                        dismissed with an × rather than a text link. */}
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="text-xs font-medium text-amber-700">
                                            {gateCount > 1 ? `Condition ${gi + 1}` : "Condition"} — flags &amp; pauses, never auto-rejects
                                        </p>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            disabled={disabled}
                                            onClick={() => removeGate(selectedStep, gi)}
                                            aria-label="Remove condition"
                                            className="h-6 w-6 flex-shrink-0 text-gray-400 hover:text-red-600"
                                        >
                                            <XIcon className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>

                                    {gate.type === "answer_knockout" ? (
                                        <div className="space-y-2">
                                            {/* The rule spelled out in words, so the merchant
                                                reads what it does before reading the fields. */}
                                            <div className="rounded-md border border-amber-200 bg-amber-100/70 px-2.5 py-1.5">
                                                <p className="text-xs font-semibold text-amber-900">
                                                    Flag WHEN the answer{" "}
                                                    <span className="font-bold">
                                                        {KNOCKOUT_OPERATOR_LABELS[gate.operator]} {gate.value.trim() || "…"}
                                                    </span>
                                                </p>
                                                <p className="mt-0.5 text-[11px] text-amber-800">
                                                    Bookings whose answer {KNOCKOUT_OPERATOR_LABELS[gate.operator]}{" "}
                                                    <span className="font-medium">{gate.value.trim() || "…"}</span>{" "}
                                                    are held for your team. Nothing is cancelled automatically.
                                                </p>
                                            </div>
                                            <Label htmlFor={`${selectedStep.id}-gate-${gi}-question`} className="block text-xs font-semibold text-amber-800">
                                                Question to watch
                                            </Label>
                                            <Select
                                                value={gate.question_ref}
                                                disabled={disabled}
                                                onValueChange={(v) => patchGate(selectedStep, gi, { question_ref: v } as Partial<BookingGate>)}
                                            >
                                                <SelectTrigger id={`${selectedStep.id}-gate-${gi}-question`} className="bg-white">
                                                    <SelectValue placeholder="Pick a question" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {(selectedStep.questions || []).map((q) => (
                                                        <SelectItem key={q.ref} value={q.ref}>{q.prompt || "(untitled question)"}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <Label className="block text-xs font-semibold text-amber-800">Flag when the answer…</Label>
                                            <div className="flex gap-2">
                                                <Select
                                                    value={gate.operator}
                                                    disabled={disabled}
                                                    onValueChange={(v) => patchGate(selectedStep, gi, { operator: v as KnockoutOperator } as Partial<BookingGate>)}
                                                >
                                                    <SelectTrigger className="w-[136px] flex-shrink-0 bg-white">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {(Object.keys(KNOCKOUT_OPERATOR_LABELS) as KnockoutOperator[]).map((op) => (
                                                            <SelectItem key={op} value={op}>{KNOCKOUT_OPERATOR_LABELS[op]}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Input
                                                    value={gate.value}
                                                    disabled={disabled}
                                                    placeholder="Yes"
                                                    onChange={(e) => patchGate(selectedStep, gi, { value: e.target.value } as Partial<BookingGate>)}
                                                    className="min-w-0 flex-1 bg-white"
                                                />
                                            </div>
                                            {/* A switch, not a checkbox: this is one setting being
                                                turned on, not an item picked from a list, and it reads
                                                the same way as every other on/off in the console. */}
                                            <div className="flex items-center justify-between gap-3">
                                                <Label htmlFor={`${selectedStep.id}-gate-${gi}-escalate`} className="cursor-pointer text-sm font-normal">
                                                    Also alert the staff channel
                                                </Label>
                                                <Switch
                                                    id={`${selectedStep.id}-gate-${gi}-escalate`}
                                                    checked={gate.escalate !== false}
                                                    disabled={disabled}
                                                    aria-label="Also alert the staff channel"
                                                    className="flex-shrink-0"
                                                    onCheckedChange={(v) => patchGate(selectedStep, gi, { escalate: v === true })}
                                                />
                                            </div>
                                            {/* The server compares the WHOLE answer, case-insensitively
                                                and with nothing else (booking_flow._compare). On a
                                                yes/no question a customer writes "Yes, I'm allergic to
                                                bleach", which does not equal "Yes", so a rule that reads
                                                naturally never fires. Say so here rather than let a
                                                merchant ship a condition that is silently dead. */}
                                            {(gate.operator === "eq" || gate.operator === "neq") && (
                                                <p className="text-xs text-amber-800">
                                                    Matches the whole answer, exactly. &ldquo;Yes&rdquo; will not catch
                                                    &ldquo;Yes, I am allergic to bleach&rdquo;, so keep the wording to what a
                                                    customer would send on its own.
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <Label htmlFor={`${selectedStep.id}-gate-${gi}-due`} className="block text-xs font-semibold text-amber-800">
                                                Finish by
                                            </Label>
                                            <Input
                                                id={`${selectedStep.id}-gate-${gi}-due`}
                                                value={gate.due}
                                                disabled={disabled}
                                                placeholder="+2h or same_day_17:00"
                                                onChange={(e) => patchGate(selectedStep, gi, { due: e.target.value } as Partial<BookingGate>)}
                                                className="bg-white"
                                            />
                                            <Label className="block text-xs font-semibold text-amber-800">Remind before the deadline</Label>
                                            <div className="flex flex-wrap gap-x-4 gap-y-2">
                                                {REMINDER_PRESETS.map((r) => {
                                                    const on = (gate.reminders || []).includes(r.value);
                                                    return (
                                                        <div key={r.value} className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id={`${selectedStep.id}-gate-${gi}-${r.value}`}
                                                                checked={on}
                                                                disabled={disabled}
                                                                className={CHECKBOX_STYLE}
                                                                onCheckedChange={() => patchGate(selectedStep, gi, {
                                                                    reminders: on
                                                                        ? (gate.reminders || []).filter((v) => v !== r.value)
                                                                        : [...(gate.reminders || []), r.value],
                                                                } as Partial<BookingGate>)}
                                                            />
                                                            <Label htmlFor={`${selectedStep.id}-gate-${gi}-${r.value}`} className="cursor-pointer text-sm font-normal">
                                                                {r.label}
                                                            </Label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-[11px] text-amber-700/80">Reminders go out as approved WhatsApp templates.</p>
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                            <div className="flex gap-3">
                                {canHaveKnockout(selectedStep.type) && (
                                    <button
                                        type="button"
                                        disabled={disabled || !(selectedStep.questions || []).length}
                                        onClick={() => addKnockout(selectedStep)}
                                        className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-40"
                                    >
                                        + Flag on an answer
                                    </button>
                                )}
                                <button
                                    type="button"
                                    disabled={disabled || stepGates(selectedStep).some((g) => g.type === "deadline")}
                                    onClick={() => addDeadline(selectedStep)}
                                    className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40"
                                >
                                    <FilterIcon className="h-3.5 w-3.5" />
                                    + Add a deadline
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Nothing selected. The panel used to go blank, which read as a
                    broken column beside a full canvas — and it hid the only
                    other way to add a step. Same palette as the hiring builder:
                    click a block on the canvas to configure it, or add one here. */}
                {selectedId === null && (
                    <div className="space-y-3">
                        <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Build</p>
                            <p className="text-xs text-gray-500">Click a block on the canvas to configure it, or add a step below.</p>
                        </div>
                        <div className="space-y-1">
                            {CATALOG.map((type) => {
                                const Icon = STEP_TYPE_ICONS[type];
                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => insertAt(flow.steps.length, type)}
                                        disabled={disabled || atCap}
                                        className={cn(
                                            "flex w-full items-start gap-3 rounded-xl p-2.5 text-left ring-1 ring-inset ring-gray-200 transition-all hover:ring-blue-500 hover:shadow-sm dark:border-gray-700",
                                            (disabled || atCap) && "pointer-events-none opacity-50",
                                        )}
                                    >
                                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E8F1FF] text-[#0A5FD1] dark:bg-blue-950/40">
                                            <Icon className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{STEP_TYPE_LABELS[type]}</p>
                                            <p className="text-xs text-gray-500">{STEP_TYPE_DESCRIPTIONS[type]}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        {atCap && <p className="text-xs text-gray-500">A flow can have at most {MAX_FLOW_STEPS} steps.</p>}
                    </div>
                )}
            </aside>
        </div>
    );
}
